import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { ArrowLeft, Building2, Loader2, Boxes, ToggleLeft, ToggleRight } from 'lucide-react'
import api from '../../core/services/api'

interface Tenant {
  id: string
  code: string
  name: string
  description: string | null
  status: string
  email: string | null
  phone: string | null
  max_users: string
  created_at: string
}

interface ModuleConfig {
  module: {
    id: string
    code: string
    name: string
    description: string | null
    category: string
    icon: string | null
    is_core: boolean
  }
  config: {
    status: string
    data_scope: string
    menu_position: string | null
    menu_order: string | null
  } | null
}

export default function TenantDetailPage() {
  const { id } = useParams()
  const navigate = useNavigate()

  const [tenant, setTenant] = useState<Tenant | null>(null)
  const [modules, setModules] = useState<ModuleConfig[]>([])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState<string | null>(null)

  useEffect(() => {
    if (id) {
      loadData()
    }
  }, [id])

  const loadData = async () => {
    try {
      const [tenantRes, modulesRes] = await Promise.all([
        api.get(`/v1/superadmin/tenants/${id}`),
        api.get(`/v1/superadmin/tenants/${id}/modules`),
      ])
      setTenant(tenantRes.data)
      setModules(modulesRes.data)
    } catch (err) {
      console.error('Errore caricamento:', err)
    } finally {
      setLoading(false)
    }
  }

  const toggleModule = async (moduleId: string, currentStatus: string | null) => {
    const newStatus = currentStatus === 'read_write' ? 'disabled' : 'read_write'

    setSaving(moduleId)
    try {
      await api.put(`/v1/superadmin/tenants/${id}/modules/${moduleId}`, {
        module_id: moduleId,
        status: newStatus,
        data_scope: 'all',
      })
      await loadData()
    } catch (err) {
      console.error('Errore configurazione modulo:', err)
    } finally {
      setSaving(null)
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
      </div>
    )
  }

  if (!tenant) {
    return <div>Tenant non trovato</div>
  }

  // Raggruppa moduli per categoria
  const groupedModules = modules.reduce((acc, m) => {
    const cat = m.module.category
    if (!acc[cat]) acc[cat] = []
    acc[cat].push(m)
    return acc
  }, {} as Record<string, ModuleConfig[]>)

  const categoryLabels: Record<string, string> = {
    core: 'Core',
    sales: 'Vendite',
    purchase: 'Acquisti',
    inventory: 'Magazzino',
    production: 'Produzione',
    tools: 'Strumenti',
    settings: 'Impostazioni',
  }

  return (
    <div>
      {/* Back */}
      <button
        onClick={() => navigate('/superadmin/tenants')}
        className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-6"
      >
        <ArrowLeft className="w-5 h-5" />
        Torna alla lista
      </button>

      {/* Header */}
      <div className="bg-white rounded-xl shadow-sm p-6 mb-6">
        <div className="flex items-center gap-4">
          <div className="w-16 h-16 bg-primary-100 rounded-xl flex items-center justify-center">
            <Building2 className="w-8 h-8 text-primary-600" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{tenant.name}</h1>
            <p className="text-gray-500">
              Codice: <span className="font-mono">{tenant.code}</span> •
              Status: <span className="capitalize">{tenant.status}</span>
            </p>
          </div>
        </div>
      </div>

      {/* Module Configuration */}
      <div className="bg-white rounded-xl shadow-sm">
        <div className="px-6 py-4 border-b flex items-center gap-2">
          <Boxes className="w-5 h-5 text-primary-600" />
          <h2 className="font-semibold text-gray-900">Configurazione Moduli</h2>
        </div>

        <div className="p-6 space-y-8">
          {Object.entries(groupedModules).map(([category, categoryModules]) => (
            <div key={category}>
              <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-4">
                {categoryLabels[category] || category}
              </h3>

              <div className="space-y-3">
                {categoryModules.map((m) => {
                  const isEnabled = m.config?.status === 'read_write' || m.config?.status === 'read_only'
                  const isCore = m.module.is_core

                  return (
                    <div
                      key={m.module.id}
                      className={`flex items-center justify-between p-4 rounded-lg border ${
                        isEnabled ? 'border-primary-200 bg-primary-50' : 'border-gray-200'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <div
                          className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                            isEnabled ? 'bg-primary-600 text-white' : 'bg-gray-100 text-gray-400'
                          }`}
                        >
                          <Boxes className="w-5 h-5" />
                        </div>
                        <div>
                          <p className="font-medium text-gray-900">
                            {m.module.name}
                            {isCore && (
                              <span className="ml-2 text-xs bg-gray-200 text-gray-600 px-2 py-0.5 rounded">
                                Core
                              </span>
                            )}
                          </p>
                          <p className="text-sm text-gray-500">{m.module.description}</p>
                        </div>
                      </div>

                      <button
                        onClick={() => !isCore && toggleModule(m.module.id, m.config?.status || null)}
                        disabled={isCore || saving === m.module.id}
                        className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                          isCore
                            ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                            : isEnabled
                            ? 'bg-primary-600 text-white hover:bg-primary-700'
                            : 'bg-gray-200 text-gray-600 hover:bg-gray-300'
                        }`}
                      >
                        {saving === m.module.id ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : isEnabled ? (
                          <ToggleRight className="w-5 h-5" />
                        ) : (
                          <ToggleLeft className="w-5 h-5" />
                        )}
                        {isEnabled ? 'Attivo' : 'Disattivo'}
                      </button>
                    </div>
                  )
                })}
              </div>
            </div>
          ))}

          {modules.length === 0 && (
            <div className="text-center py-8 text-gray-500">
              <Boxes className="w-12 h-12 mx-auto mb-4 text-gray-300" />
              <p>Nessun modulo nel registry.</p>
              <p className="text-sm">Vai alla sezione Moduli per inizializzarli.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
